import { FC, useCallback } from 'react';
import './DApp.css';
import { sendTransaction, useProvider } from './useProvider';
import { Field } from './Field';
import {
  useBalance,
  useDefaultGasFee,
  useDefaultNonce,
  useValues,
} from './useValues';
import { ethers } from 'ethers';
import { useSigner } from './useSigner';

export interface DAppProps {}

function formatUnits(value: string, unit: string): string {
  return value ? ethers.utils.formatUnits(value, unit) : value;
}

export const DApp: FC<DAppProps> = () => {
  const {
    to,
    setTo,
    nonce,
    setNonce,
    value,
    setValue,
    chainId,
    setChainId,
    maxFeePerGas,
    setMaxFeePerGas,
    maxPriorityFeePerGas,
    setMaxPriorityFeePerGas,
    balance,
    setBalance,
  } = useValues();

  const [provider] = useProvider();
  const [signer, setSigner] = useSigner(provider);

  const handleSendTransaction = useCallback(async () => {
    if (!value || !to) {
      alert('Please fill the required fields');
      return;
    }

    try {
      const receipt = await sendTransaction(provider, {
        to,
        value,
        nonce,
        maxFeePerGas,
        maxPriorityFeePerGas,
      });
      if (!receipt) {
        alert('Send transaction failed.');
        return;
      }

      alert('Send transaction successfully.');
    } catch (error) {
      console.error(error);
      alert('Send transaction failed.');
    }
  }, [value, to, nonce, maxFeePerGas, maxPriorityFeePerGas, provider]);

  const handleSwitchChain = useCallback(async () => {
    provider
      ?.send('wallet_switchEthereumChain', [{ chainId }])
      .then(() => {
        setSigner(provider.getSigner());
      })
      .catch((error) => {
        console.error(error);
        alert(`Switch chain failed.\n ${error.message}`);
      });
  }, [provider, chainId, setSigner]);

  useDefaultNonce(signer, setNonce);
  useDefaultGasFee(signer, setMaxFeePerGas, setMaxPriorityFeePerGas);
  useBalance(signer, setBalance);

  return (
    <div className="DApp">
      <h1> DApp </h1>
      <h3>BASIC</h3>
      <Field
        label="balance(ether)"
        value={formatUnits(balance, 'ether')}
        updater={setBalance}
        disabled={true}
      />
      <Field label="to" value={to} updater={setTo} required={true} />
      <Field label="value" value={value} updater={setValue} required={true} />
      <Field label="nonce" value={nonce} updater={setNonce} />
      <h3>SWITCH CHAIN</h3>
      <Field label="chain id" value={chainId} updater={setChainId} />
      <button onClick={handleSwitchChain}>Switch</button>
      <h3>GAS FEE</h3>
      <Field
        label="maxFeePerGas(gwei)"
        value={formatUnits(maxFeePerGas, 'gwei')}
        updater={setMaxFeePerGas}
      />
      <Field
        label="maxPriorityFeePerGas(gwei)"
        value={formatUnits(maxPriorityFeePerGas, 'gwei')}
        updater={setMaxPriorityFeePerGas}
      />
      <button onClick={handleSendTransaction}>Send</button>
    </div>
  );
};
